import type { LoginRequest } from '@/types/requests/loginRequest'
import type { LoginResponse } from '@/types/responses/loginResponse'
import type { MfaVerificationRequest } from '@/types/requests/mfaVerificationRequest'
import type { MfaVerificationResponse } from '@/types/responses/mfaVerificationResponse'
import type { SendMfaRequest } from '@/types/requests/sendMfaRequest'

import type { RegisterRequest } from '@/types/requests/registerRequest'
import type { RegisterResponse } from '@/types/responses/registerResponse'

import { getActivePinia } from 'pinia'

export const useUserStore = defineStore('user', {
  state: (): {
    user: LoginResponse
    loggingOut: boolean
  } => ({
    user: {} as LoginResponse,
    loggingOut: false
  }),
  getters: {
    isAuthenticated: (state) => Boolean(state.user?.isAuthenticated)
  },
  actions: {
    clearAuth() {
      this.user.isAuthenticated = false
    },
    clearUser() {
      // More comprehensive user data clearing
      this.user = {} as LoginResponse
    },
    async setUser(payload: LoginResponse) {
      this.user = payload
    },
    async loginUser(payload: LoginRequest): Promise<LoginResponse> {
      const { response } = useApiFetch(`user/login`, {
        method: 'POST',
        body: payload
      })
      return (await response) as LoginResponse
    },
    async sendMfa(payload: SendMfaRequest): Promise<void> {
      const { response } = useApiFetch(`user/login/sendmfa`, {
        method: 'POST',
        body: payload
      })
      await response
    },
    async verifyMfa(payload: MfaVerificationRequest): Promise<MfaVerificationResponse> {
      const { response } = useApiFetch(`user/login/mfa`, {
        method: 'POST',
        body: payload
      })
      return (await response) as MfaVerificationResponse
    },
    async resendMfaCode(payload: SendMfaRequest): Promise<void> {
      // Resend MFA code by calling the sendmfa endpoint again
      const { response } = useApiFetch(`user/login/sendmfa`, {
        method: 'POST',
        body: payload
      })
      await response
    },
    async logoutUser({ redirectQuery = false }: { redirectQuery?: boolean } = {}): Promise<
      ReturnType<typeof navigateTo>
    > {
      // Prevent re-entrancy: if already logging out, return early
      if (this.loggingOut) {
        return
      }

      // Immediately set flags to prevent inactivity monitor from triggering another logout
      this.loggingOut = true
      this.clearAuth()

      try {
        // Use useApi with suppressGlobalErrorModal to include auth headers but avoid error modals
        const { response } = useApi('user/logout', { method: 'DELETE', suppressGlobalErrorModal: true })

        await response
          .then(async () => {
            const activePinia = getActivePinia()
            if (activePinia) {
              // @ts-expect-error don't feel like typing this
              activePinia._s.forEach((store) => store.$reset())
            }

            // Shutdown Intercom on logout
            const { shutdown } = useIntercomManager()
            shutdown()
          })
          .catch(async (error) => {
            // Log the error but proceed with local logout regardless
            const status = error?.response?.status || error?.status || error?.statusCode
            console.log('Logout API call failed with status:', status, '- proceeding with local logout')
          })
          .finally(async () => {
            const { clearCookies, loginRedirect } = useAuth()

            clearCookies()
            await loginRedirect(redirectQuery ? { redirectQuery: true } : {})
          })
      } finally {
        // Reset the loggingOut flag in case user logs back in
        this.loggingOut = false
      }
    },
    async registerUser(payload: RegisterRequest): Promise<RegisterResponse> {
      const { response } = useApiFetch(`user/signup`, {
        method: 'POST',
        body: payload
      })
      return (await response) as RegisterResponse
    },
    async sendActivationEmail(payload: { eMail: string; username: string }): Promise<void> {
      const { response } = useApiFetch(`user/signup/send-activation-email`, {
        method: 'POST',
        body: payload
      })
      await response
    },
    async forgotUsername(payload: { emailAddress: string }): Promise<void> {
      const { response } = useApiFetch(`user/account/forgotusername`, {
        method: 'POST',
        body: payload
      })
      await response
    },
    async forgotPassword(payload: { eMail: string }): Promise<void> {
      const { response } = useApiFetch(`user/account/forgotpassword`, {
        method: 'POST',
        body: payload
      })
      await response
    },
    async resetPassword(payload: { password: string; passwordConfirm: string }, token: string): Promise<void> {
      const { response } = useApiFetch(`user/account/resetpassword`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: payload
      })
      await response
    },
    async changePassword(
      payload: { password: string; passwordConfirm: string; oldPassword: string },
      context: 'page' | 'dialog' = 'page'
    ): Promise<void> {
      if (payload.password !== payload.passwordConfirm) {
        throw new Error('Passwords do not match')
      }

      const { response } = useApi(
        `user/account/changepassword`,
        {
          method: 'POST',
          body: {
            password: payload.password,
            passwordConfirm: payload.password,
            oldPassword: payload.oldPassword
          }
        },
        context
      )
      await response
    }
  },
  persist: {
    omit: ['user.bearerToken', 'user.refreshToken', 'loggingOut'],
    storage: piniaPluginPersistedstate.cookies({
      expires: useCookieExpire.weeks(1)
    })
  }
})
